<template>
  <div>
    <b-button class="p-2 fz-inherit" variant="outline-secondary" @click="open">
      <div class="d-flex align-items-center">
        <mdicon name="PercentOutline" class="color-black mr-2" size="36" />
        <span class="color-black">Configure</span>
      </div>
    </b-button>

    <b-modal :id="modalId" v-model="isOpen" centered>
      <template #title>Cost margins</template>

      <p class="text-muted mb-3">
        Set the percentage added to the carrier cost for this customer. Changes only affect new price calculations.
      </p>

      <div v-if="isLoading" class="cost-margin-state text-muted" role="status">
        <b-spinner small class="mr-2" />
        Loading margins…
      </div>

      <div v-else-if="errorMessage && !margins.length" class="alert alert-danger mb-0" role="alert">
        {{ errorMessage }}
      </div>

      <div v-else class="cost-margin-list">
        <div v-for="margin in margins" :key="margin.carrierHandle" class="cost-margin-row">
          <div class="cost-margin-copy">
            <div class="d-flex align-items-center">
              <label :for="inputId(margin.carrierHandle)" class="font-weight-bold mb-0">
                {{ margin.name }}
              </label>
              <span
                class="badge ml-2"
                :class="margin.isCustom ? 'bg-primary' : 'bg-light text-dark'"
              >
                {{ margin.isCustom ? 'Custom' : 'Default' }}
              </span>
            </div>
            <small class="text-muted">
              System default: {{ defaultMarginPercent }}%
            </small>
          </div>

          <div class="input-group cost-margin-input">
            <input
              :id="inputId(margin.carrierHandle)"
              v-model.number="margin.marginPercent"
              class="form-control text-right"
              type="number"
              min="0"
              max="100"
              step="1"
              inputmode="numeric"
              :aria-label="`${margin.name} cost margin percent`"
            />
            <span class="input-group-text">%</span>
          </div>
        </div>
      </div>

      <div v-if="errorMessage && margins.length" class="alert alert-danger mt-3 mb-0" role="alert">
        {{ errorMessage }}
      </div>

      <template #footer>
        <div class="d-flex justify-content-end w-100">
          <b-button class="btn-grey" :disabled="isSaving" @click="isOpen = false">
            Cancel
          </b-button>
          <b-button
            class="btn-green ml-2"
            variant="success"
            :disabled="!canSave"
            @click="save"
          >
            <b-spinner v-if="isSaving" small class="mr-1" />
            {{ isSaving ? 'Saving…' : 'Save changes' }}
          </b-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script lang="ts">
import type { Company } from '@/types/Models/Company'
import type { CostMargin, CostMarginsResponse, CostMarginUpdate } from '@/types/Models/CostMargin'
import { notify } from '@kyvg/vue3-notification'
import { BButton, BModal, BSpinner } from 'bootstrap-vue-next'
import { computed, defineComponent, ref, type PropType, type Ref } from 'vue'
import CostMarginsProxy from '@/proxies/Customer/CostMarginsProxy'

export default defineComponent({
  name: 'CostMarginsModal',
  components: {
    BButton,
    BModal,
    BSpinner,
  },
  props: {
    company: {
      type: Object as PropType<Company>,
      required: true,
    },
  },
  setup(props) {
    const isOpen = ref(false)
    const isLoading = ref(false)
    const isSaving = ref(false)
    const errorMessage = ref('')
    const margins: Ref<CostMargin[]> = ref([])
    const originalMargins: Ref<Record<string, number>> = ref({})
    const defaultMarginPercent = ref(20)
    const modalId = `cost-margins-modal-${props.company.id}`

    const inputId = (carrierHandle: string) => `${modalId}-${carrierHandle}`

    const applyResponse = (response: CostMarginsResponse) => {
      margins.value = response.data.map((margin) => ({ ...margin }))
      originalMargins.value = Object.fromEntries(
        response.data.map((margin) => [margin.carrierHandle, margin.marginPercent]),
      )
      defaultMarginPercent.value = response.meta.defaultMarginPercent
    }

    const changedMargins = computed<CostMarginUpdate[]>(() => {
      return margins.value
        .filter((margin) => originalMargins.value[margin.carrierHandle] !== margin.marginPercent)
        .map((margin) => ({
          carrierHandle: margin.carrierHandle,
          marginPercent: margin.marginPercent,
        }))
    })

    const hasValidMargins = computed(() => {
      return margins.value.every((margin) => {
        return Number.isInteger(margin.marginPercent)
          && margin.marginPercent >= 0
          && margin.marginPercent <= 100
      })
    })

    const canSave = computed(() => {
      return !isLoading.value && !isSaving.value && hasValidMargins.value && changedMargins.value.length > 0
    })

    const fetchMargins = async () => {
      isLoading.value = true
      errorMessage.value = ''

      try {
        const response = await new CostMarginsProxy(props.company.id).all()
        applyResponse(response)
      } catch (error: any) {
        errorMessage.value = error?.message || 'Could not load cost margins.'
      } finally {
        isLoading.value = false
      }
    }

    const open = () => {
      isOpen.value = true
      fetchMargins()
    }

    const save = async () => {
      if (!canSave.value) return

      isSaving.value = true
      errorMessage.value = ''

      try {
        const response = await new CostMarginsProxy(props.company.id).save(changedMargins.value)
        applyResponse(response)
        notify({
          group: 'success',
          text: 'Cost margins updated',
        })
        isOpen.value = false
      } catch (error: any) {
        errorMessage.value = error?.message || 'Could not update cost margins.'
        notify({
          group: 'error',
          text: errorMessage.value,
        })
      } finally {
        isSaving.value = false
      }
    }

    return {
      canSave,
      defaultMarginPercent,
      errorMessage,
      inputId,
      isLoading,
      isOpen,
      isSaving,
      margins,
      modalId,
      open,
      save,
    }
  },
})
</script>

<style scoped>
.cost-margin-list {
  border: 1px solid #e1e5ea;
  border-radius: 0.5rem;
  max-height: min(55vh, 33rem);
  overflow-y: auto;
}

.cost-margin-row {
  align-items: center;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.cost-margin-row + .cost-margin-row {
  border-top: 1px solid #e1e5ea;
}

.cost-margin-copy {
  min-width: 0;
}

.cost-margin-input {
  flex: 0 0 8rem;
}

.cost-margin-input .form-control {
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  min-width: 0;
}

.cost-margin-input .input-group-text {
  align-items: center;
  border: 1px solid #ccc;
  border-left: 0;
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  display: flex;
  flex: 0 0 2.75rem;
  justify-content: center;
}

.cost-margin-state {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 7rem;
}

@media (max-width: 575.98px) {
  .cost-margin-row {
    align-items: stretch;
    flex-direction: column;
  }

  .cost-margin-input {
    flex-basis: auto;
    width: 100%;
  }
}
</style>
